import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Seo from "../components/Seo";

export default function About() {
  return (
    <>
      <Seo
        title="About Zero Harm AI | Agentic AI & AI Security Development"
        description="Zero Harm AI designs and builds AI agents and AI security solutions for production. Creator of the ZeroHarmAI open-source security toolkit."
        socialDescription="Agentic AI and AI security development backed by the ZeroHarmAI open-source security toolkit."
        path="/about"
      />

      <div className="p-4 md:p-6 max-w-5xl mx-auto">
        <motion.h1
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-3xl md:text-4xl font-bold mb-4 text-center bg-linear-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent"
        >
          About Zero Harm AI
        </motion.h1>

        <p className="text-base md:text-lg font-semibold text-indigo-700 mb-2 text-center">
          Agentic AI &amp; AI Security Development
        </p>

        <p className="text-sm md:text-base italic text-gray-500 mb-6 text-center">
          Creator of the ZeroHarmAI open-source security toolkit
        </p>

        <p className="text-base text-gray-700 mb-8 text-center max-w-3xl mx-auto">
          We build AI agents and AI security solutions for engineering teams, taking agentic
          systems from prototype to production with sound architecture, enforceable guardrails,
          and reliable operations.
        </p>

        {/* Mission */}
        <section className="mb-10 bg-linear-to-r from-blue-50 to-indigo-50 p-6 rounded-xl">
          <h2 className="text-2xl font-bold mb-3 text-center">Our Mission</h2>
          <p className="text-gray-700 text-center max-w-2xl mx-auto">
            To help organizations build AI agents that are capable, secure, observable,
            and trustworthy in production.
          </p>
        </section>

        {/* What We Do */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-center">What We Do</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-blue-50 p-6 rounded-xl shadow-md">
              <div className="text-3xl mb-2">🤖</div>
              <h3 className="text-lg font-bold mb-2 text-blue-800">AI Agent Development</h3>
              <p className="text-gray-700 mb-3 text-sm">
                Design and build custom AI agents and agentic workflows around real business processes.
              </p>
              <ul className="text-gray-700 space-y-1 text-sm">
                <li>• Custom AI agents and copilots</li>
                <li>• Multi-agent and RAG workflows</li>
                <li>• MCP servers and tool integrations</li>
                <li>• Identity, permissions, and data boundaries</li>
                <li>• Evaluations and scalable cloud deployment</li>
              </ul>
            </div>

            <div className="bg-purple-50 p-6 rounded-xl shadow-md">
              <div className="text-3xl mb-2">🛡️</div>
              <h3 className="text-lg font-bold mb-2 text-purple-800">AI Security Development</h3>
              <p className="text-gray-700 mb-3 text-sm">
                Build the guardrails and security tooling that keep AI applications and agents safe.
              </p>
              <ul className="text-gray-700 space-y-1 text-sm">
                <li>• Agent and MCP threat modeling</li>
                <li>• Prompt-injection and tool-abuse defenses</li>
                <li>• PII, secret, and harmful-content redaction</li>
                <li>• Red-team and adversarial testing</li>
                <li>• Custom AI security tooling and CI checks</li>
              </ul>
            </div>

            <div className="bg-green-50 p-6 rounded-xl shadow-md">
              <div className="text-3xl mb-2">⚙️</div>
              <h3 className="text-lg font-bold mb-2 text-green-800">Secure Agent Operations</h3>
              <p className="text-gray-700 mb-3 text-sm">
                Run agents in production with the delivery, monitoring, and governance they need to stay reliable.
              </p>
              <ul className="text-gray-700 space-y-1 text-sm">
                <li>• Secure AI CI/CD controls</li>
                <li>• Cloud and Kubernetes deployment</li>
                <li>• Observability and reliability</li>
                <li>• Governance automation</li>
                <li>• Continuous monitoring and response</li>
              </ul>
            </div>
          </div>
        </section>

        {/* How We Work */}
        <section className="mb-10 bg-gray-50 p-6 rounded-xl">
          <h2 className="text-2xl font-bold mb-6 text-center">How We Work</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-3xl mb-2">1️⃣</div>
              <h3 className="text-base font-bold mb-1">Discovery</h3>
              <p className="text-gray-700 text-sm">
                Map the workflow you want to automate, the data involved, and the risks that matter.
              </p>
            </div>
            <div className="text-center">
              <div className="text-3xl mb-2">2️⃣</div>
              <h3 className="text-base font-bold mb-1">Design</h3>
              <p className="text-gray-700 text-sm">
                Agent architecture, tool and permission boundaries, guardrails, and a threat model with clear milestones.
              </p>
            </div>
            <div className="text-center">
              <div className="text-3xl mb-2">3️⃣</div>
              <h3 className="text-base font-bold mb-1">Build</h3>
              <p className="text-gray-700 text-sm">
                Iterative agent development with evaluations, security tests, and regular demos.
              </p>
            </div>
            <div className="text-center">
              <div className="text-3xl mb-2">4️⃣</div>
              <h3 className="text-base font-bold mb-1">Launch & Operate</h3>
              <p className="text-gray-700 text-sm">
                Secure deployment, monitoring, and ongoing tuning as your agents take on more work.
              </p>
            </div>
          </div>
        </section>

        {/* Technology Expertise */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-center">Technology Expertise</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white p-5 rounded-xl shadow-md">
              <h3 className="font-bold mb-2 text-blue-700">AI Platform Engineering</h3>
              <p className="text-sm text-gray-600">
                Python, TypeScript, APIs, distributed systems, vector databases, RAG, cloud platforms
              </p>
            </div>
            <div className="bg-white p-5 rounded-xl shadow-md">
              <h3 className="font-bold mb-2 text-indigo-700">Agentic Systems</h3>
              <p className="text-sm text-gray-600">
                LLMs, agents, MCP, tool orchestration, evaluations, permissions, prompt and policy controls
              </p>
            </div>
            <div className="bg-white p-5 rounded-xl shadow-md">
              <h3 className="font-bold mb-2 text-purple-700">Security & DevSecOps</h3>
              <p className="text-sm text-gray-600">
                AWS, GCP, Azure, Docker, Kubernetes, Terraform, GitHub Actions, SAST, DAST, compliance controls
              </p>
            </div>
          </div>
        </section>

        {/* Who We Serve */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-6 text-center">Who We Serve</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition">
              <div className="text-4xl mb-3">🚀</div>
              <h3 className="text-lg font-bold mb-2">Startups</h3>
              <p className="text-gray-600 text-sm">
                AI agents and security foundations for AI-native products.
              </p>
            </div>
            <div className="text-center p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition">
              <div className="text-4xl mb-3">📈</div>
              <h3 className="text-lg font-bold mb-2">Growing SMBs</h3>
              <p className="text-gray-600 text-sm">
                Agent automation, AI platform integration, and secure operations as adoption grows.
              </p>
            </div>
            <div className="text-center p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition">
              <div className="text-4xl mb-3">🏢</div>
              <h3 className="text-lg font-bold mb-2">Enterprises</h3>
              <p className="text-gray-600 text-sm">
                Agent development, AI security controls, and security assessments
                for complex environments.
              </p>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="mb-10 bg-linear-to-r from-blue-600 to-indigo-600 text-white p-8 rounded-xl">
          <h2 className="text-2xl font-bold mb-6 text-center">Why Choose Zero Harm AI?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            <div>
              <h3 className="text-lg font-bold mb-1">✓ Enterprise-Grade Quality</h3>
              <p className="text-blue-100 text-sm">
                Code quality and architecture that stands up to enterprise standards.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-bold mb-1">✓ Security First</h3>
              <p className="text-blue-100 text-sm">
                Built-in security, compliance, and data protection from day one.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-bold mb-1">✓ Scalable Architecture</h3>
              <p className="text-blue-100 text-sm">
                Cloud-native design that grows with your business.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-bold mb-1">✓ Evidence-Driven Assurance</h3>
              <p className="text-blue-100 text-sm">
                Practical findings, measurable controls, and clear deployment decisions.
              </p>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="text-center">
          <h2 className="text-2xl font-bold mb-3">Build AI Agents You Can Trust in Production</h2>
          <p className="text-gray-700 max-w-2xl mx-auto mb-6 text-sm">
            Whether you need a new AI agent, guardrails for an existing LLM app, or an agent
            security assessment, we can help you ship with confidence.
          </p>
          <Link
            to="/contact"
            className="inline-block px-6 py-2.5 bg-blue-600 text-white rounded-lg shadow-lg hover:bg-blue-700 font-semibold transition-all transform hover:scale-105"
          >
            Schedule Consultation
          </Link>
        </section>
      </div>
    </>
  );
}
